import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { Helmet } from 'react-helmet';
import { graphql, Link as GatsbyLink } from 'gatsby';
import {
  Absolute,
  Box,
  Container,
  Flex,
  HeadingDisplay,
  HeadingH2,
  useBreakpointValue,
  useToken,
  Button,
  Image,
  ListItem,
  ListIcon,
  List,
  Divider,
  Relative,
  Circle,
  Text,
  Grid,
  Link,
  VStack,
} from '@hausgold/designsystem';
import { CheckCircled, Check } from '@hausgold/designsystem/icons';
import Layout from '../../components/Layout';
import Header from '../../components/Header';
import Footer from '../../components/partner-werden-prototype/Footer';
import SEO from '../../components/SEO';
import ExitIntentModal from '../../components/ExitIntentModal';
import ExitIntent from '../../components/ExitIntent';
import EmiLogo from '../../components/heyemi/logo/EmiLogo';
import SoundbarIcon from '../../components/heyemi/icons/SoundbarIcon';
import SpeechTelephone from '../../components/heyemi/icons/SpeechTelephone';
import Arrow from '../../components/heyemi/icons/Arrow';
import CalendarUser from '../../components/heyemi/icons/CalendarUser';
import HandshakeTarget from '../../components/heyemi/icons/HandshakeTarget';
import {
  analyticsTrack,
  EVENT_OBJECT,
  EVENT_VERB,
  eventBuilder,
} from '../../utils/tracking';

const Heyemi = ({ data, location }) => {
  const page = get(data, 'contentfulPage');
  const site = get(data, 'site');
  const exitIntent = get(page, 'exitIntent');

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const removeExitIntent = ExitIntent({
      threshold: 10,
      eventThrottle: 100,
      onExitIntent: () => {
        setShowPopup(true);
      },
    });
    return () => {
      removeExitIntent();
    };
  }, []);

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Startseite',
        url: `${site.siteMetadata.siteUrl}/`,
      },
      {
        title: page.title,
        url: `${site.siteMetadata.siteUrl}/fuer-makler/${page.slug}/`,
      },
    ],
    [page.slug, page.title, site.siteMetadata.siteUrl]
  );

  const BP_DESKTOP = '2xl';

  const isDesktopView = useBreakpointValue({ base: false, xl: true });

  // We need the css compatible color values for the gradient
  const [purpleColor, purpleColor2] = useToken('colors', [
    'purple.400',
    'purple.500',
  ]);
  const purpleGradient = `linear-gradient(124deg, ${purpleColor} 50%, ${purpleColor2})`;

  const purpleBackgroundStyle = useMemo(
    () => ({
      background: purpleGradient,
      textColor: 'white',
    }),
    [purpleGradient]
  );

  const orangeBackgroundStyle = useMemo(
    () => ({
      backgroundColor: 'orange.400',
    }),
    []
  );

  const listIconStyle = useMemo(
    () => ({
      color: 'orange.400',
      boxSize: 4,
    }),
    []
  );

  const iconCircleStyle = useMemo(
    () => ({
      fontWeight: 'bold',
      fontSize: 'xl',
      padding: 1,
      ...orangeBackgroundStyle,
    }),
    [orangeBackgroundStyle]
  );

  const sectionStyle = useMemo(
    () => ({
      paddingY: { base: '3rem', [BP_DESKTOP]: '5rem' },
    }),
    []
  );

  const pageHeadlineStyle = useMemo(
    () => ({
      // 54/40px // 800
      marginBottom: { base: 6, [BP_DESKTOP]: 8 },
      fontSize: { base: '2.5rem', [BP_DESKTOP]: '3.375rem' },
      fontWeight: '800',
    }),
    []
  );

  const headlineStyle = useMemo(
    () => ({
      // 48/32 px // 700
      fontSize: { base: '2rem', [BP_DESKTOP]: '3rem' },
      fontWeight: 'bold',
      marginBottom: 8,
    }),
    []
  );

  const priceStyle = useMemo(
    () => ({
      // 48/40 px // 700
      fontWeight: 'bold',
      fontSize: { base: '2.5rem', [BP_DESKTOP]: '3rem' },
    }),
    []
  );

  const textXSBoldStyle = useMemo(
    () => ({
      // 16/14 px // 600/400
      fontWeight: { base: 'normal', [BP_DESKTOP]: '600' },
      fontSize: { base: 'md', [BP_DESKTOP]: 'lg' },
    }),
    []
  );

  const textSMStyle = useMemo(
    () => ({
      // 18/14 px // 400
      fontWeight: 'normal',
      fontSize: { base: 'md', [BP_DESKTOP]: 'xl' },
      marginBottom: { base: 6, [BP_DESKTOP]: 8 },
    }),
    []
  );

  const textSMBoldStyle = useMemo(
    () => ({
      // 18/16 px // 800/600
      fontSize: { base: 'lg', [BP_DESKTOP]: 'xl' },
      fontWeight: { base: '600', [BP_DESKTOP]: '800' },
    }),
    []
  );

  const textSMAlwaysBolder = useMemo(
    () => ({
      // 18/14 px // 800
      fontWeight: '800',
      fontSize: { base: 'md', [BP_DESKTOP]: 'xl' },
    }),
    []
  );

  const textSMAlwaysMDBolder = useMemo(
    () => ({
      // 18/14 px / 600
      ...textSMAlwaysBolder,
      fontWeight: '600',
    }),
    []
  );

  const textLGStyle = useMemo(
    () => ({
      // 24/14 px // 400
      fontSize: { base: 'md', [BP_DESKTOP]: '1.5rem' },
      fontWeight: 'normal',
    }),
    []
  );

  const textLGBoldStyle = useMemo(
    () => ({
      // 24/18 px // 700
      fontWeight: 'bold',
      fontSize: { base: 'xl', [BP_DESKTOP]: '1.5rem' },
    }),
    []
  );

  const textXLBoldStyle = useMemo(
    () => ({
      // 48/32 px // 700
      fontWeight: 'bold',
      fontSize: { base: '2rem', [BP_DESKTOP]: '3rem' },
    }),
    []
  );

  const buttonStyle = useMemo(
    () => ({
      borderRadius: 'xl',
      color: 'white',
      ...textSMBoldStyle,
      backgroundColor: 'orange.400',
      borderColor: 'orange.400',
      _hover: {
        color: 'orange.400',
        backgroundColor: 'white',
      },
      _active: {
        color: 'orange.400',
        backgroundColor: 'white',
      },
      _focus: {
        color: 'orange.400',
        backgroundColor: 'white',
      },
      whiteSpace: 'wrap',
    }),
    [textSMBoldStyle]
  );

  const handleClick = () => {
    analyticsTrack({
      event: eventBuilder(EVENT_OBJECT.button, EVENT_VERB.interacted),
      params: {
        label: 'Kostenlosen Zugang sichern',
        to: '/koop-anfrage/',
      },
    });
  };

  return (
    <Layout
      location={location}
      cookieConsent
      header={
        <>
          <Header template="Makler" headerStyles={{ marginTop: 0 }} />
        </>
      }
      footer={<Footer />}
      contentStyles={{ mb: 0 }}
    >
      <Helmet>
        <meta name="content-entity" content="page" />
        <meta name="content-id" content={page.contentful_id} />
      </Helmet>
      <SEO
        entity={{ ...page }}
        title={page.metaTitle || page.headline}
        canonical={page.canonical}
        type="page"
        description={page.metaDescription}
        indexable={page.indexable}
        breadcrumbs={breadcrumbs}
      />

      {/* Section 1 - Eye catcher */}
      <Box
        textColor="white"
        backgroundRepeat="no-repeat"
        backgroundBlendMode="soft-light"
        backgroundPosition="bottom"
        backgroundSize="cover"
        /*
         * The used chakra-ui version has a bug which would transform "url" to "url-gradiant" which is invalid.
         * So, instead of using "backgroundImage" directly we use "style" instead.
         * See https://github.com/chakra-ui/chakra-ui/issues/7548
         */
        style={{
          backgroundImage: `url(/heyemi/bg-section1.png),${purpleGradient}`,
        }}
      >
        <Container>
          <Box
            // Adds space for page navigation.
            paddingTop={100}
          >
            <Flex justifyContent="space-between">
              <Box alignContent="center">
                <EmiLogo />
              </Box>
            </Flex>
          </Box>
          <Flex
            alignItems="center"
            paddingTop={10}
            flexDirection={{ base: 'column', xl: 'row' }}
          >
            <Box marginRight={{ base: 0, xl: 8 }}>
              <HeadingDisplay {...pageHeadlineStyle}>
                Mehr Termine, weniger Aufwand - emi telefoniert für Sie
              </HeadingDisplay>
              <HeadingH2 {...textSMStyle}>
                Ihre KI-Assistentin ruft Eigentümer an, vereinbart
                Bewertungstermine und trägt sie direkt in Ihren Kalender ein -
                vollautomatisch.
              </HeadingH2>
              <Button
                as={GatsbyLink}
                to="/koop-anfrage/"
                onClick={handleClick}
                marginBottom={8}
                {...buttonStyle}
              >
                Kostenlosen Zugang sichern
              </Button>
            </Box>
            <Box
              position="relative"
              maxWidth={{ base: '300px', xl: '380px' }}
              minWidth={{ base: '0px', xl: '380px' }}
            >
              <Image
                src="/heyemi/emi-telephone.png"
                alt="woman on a smartphone"
                width="100%"
              />
              <Absolute bottom={0} justifySelf="anchor-center">
                <Flex
                  {...purpleBackgroundStyle}
                  paddingX={{ base: 2, xl: 6 }}
                  paddingY={4}
                  flexDirection="column"
                >
                  <Box>
                    <SoundbarIcon />
                  </Box>
                  <Box {...textLGBoldStyle}>Hey, ich bin emi,</Box>
                  <Box {...textSMAlwaysMDBolder}>
                    Ihre persönliche KI-Assistentin
                  </Box>
                </Flex>
              </Absolute>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Section 2 - Benefits */}
      <Box {...sectionStyle}>
        <Container>
          <HeadingDisplay as="h2" justifySelf="center" {...headlineStyle}>
            Ohne heyemi ist es Aufwand. Mit heyemi läuft’s automatisch.
          </HeadingDisplay>
          {/*
           * As the list items should be aligned next to each other, but code-wise they have no relation to each other,
           * we are adding hidden dummy text, so all texts row-wise have the same length which makes same behave equal
           * regarding line breaks.
           */}
          <Flex
            flexDirection={{ base: 'column', xl: 'row' }}
            justifyContent="space-between"
          >
            <Box
              padding={8}
              boxShadow="md"
              width={{ base: '100%', xl: '48%' }}
              marginBottom={6}
            >
              <Box
                backgroundColor="red.100"
                rounded="xl"
                paddingX={3}
                paddingY={1}
                marginBottom={4}
                width="fit-content"
              >
                <Flex alignItems="center">
                  <Circle
                    marginRight={1}
                    backgroundColor="red.500"
                    boxSize={4}
                  />
                  <Text
                    marginBottom={0}
                    color="red.500"
                    fontSize="lg"
                    fontWeight="bold"
                  >
                    Ohne heyemi
                  </Text>
                </Flex>
              </Box>
              <Flex marginBottom={4}>
                <Text marginBottom={0} {...textLGBoldStyle}>
                  Typische Hürden bei der Objektakquise
                </Text>
              </Flex>
              <VStack {...textXSBoldStyle} alignItems="start">
                <Flex>
                  <Circle
                    marginTop={1}
                    marginRight={2}
                    backgroundColor="red.100"
                    boxSize={4}
                  >
                    <Text
                      marginBottom={0}
                      textColor="red.500"
                      fontSize="lg"
                      fontWeight="bold"
                    >
                      &minus;
                    </Text>
                  </Circle>
                  <Text marginBottom={0} {...textXSBoldStyle}>
                    Viele Eigentümer sind schwer erreichbar – Rückrufe bleiben
                    aus
                  </Text>
                  <Text
                    marginBottom={0}
                    {...textXSBoldStyle}
                    visibility="hidden"
                    display={{ base: 'none', xl: 'inherit' }}
                  >
                    Loremi
                  </Text>
                </Flex>
                <Flex>
                  <Circle
                    marginTop={1}
                    marginRight={2}
                    backgroundColor="red.100"
                    boxSize={4}
                  >
                    <Text
                      marginBottom={0}
                      textColor="red.500"
                      fontSize="lg"
                      fontWeight="bold"
                    >
                      &minus;
                    </Text>
                  </Circle>
                  <Text marginBottom={0} {...textXSBoldStyle}>
                    Gespräche werden nach Bauchgefühl statt datenbasiert geführt
                  </Text>
                  <Text
                    marginBottom={0}
                    {...textXSBoldStyle}
                    visibility="hidden"
                    display={{ base: 'none', xl: 'inherit' }}
                  >
                    L
                  </Text>
                </Flex>
                <Flex>
                  <Circle
                    marginTop={1}
                    marginRight={2}
                    backgroundColor="red.100"
                    boxSize={4}
                  >
                    <Text
                      marginBottom={0}
                      textColor="red.500"
                      fontSize="lg"
                      fontWeight="bold"
                    >
                      &minus;
                    </Text>
                  </Circle>
                  <Text marginBottom={0} {...textXSBoldStyle}>
                    Hoher Arbeitsaufwand pro bearbeitetem Eigentümerkontakt
                  </Text>
                </Flex>
              </VStack>
            </Box>

            <Box
              padding={8}
              boxShadow="md"
              width={{ base: '100%', xl: '48%' }}
              marginBottom={6}
            >
              <Box
                backgroundColor="purple.25"
                rounded="xl"
                paddingX={3}
                paddingY={1}
                marginBottom={4}
                width="fit-content"
              >
                <Flex alignItems="center">
                  <Circle
                    marginRight={1}
                    backgroundColor="purple.400"
                    boxSize={4}
                  />
                  <Text
                    marginBottom={0}
                    color="purple.400"
                    fontSize="lg"
                    fontWeight="bold"
                  >
                    Mit heyemi
                  </Text>
                </Flex>
              </Box>
              <Flex marginBottom={4}>
                <Text marginBottom={0} {...textLGBoldStyle}>
                  So läuft’s automatisch besser
                </Text>
                <Text
                  marginBottom={0}
                  {...textLGBoldStyle}
                  visibility="hidden"
                  display={{ base: 'none', xl: 'inherit' }}
                >
                  Lorum ip
                </Text>
              </Flex>
              <VStack {...textXSBoldStyle} alignItems="start">
                <Flex>
                  <Circle
                    marginTop={1}
                    marginRight={2}
                    backgroundColor="purple.25"
                    boxSize={4}
                  >
                    <Check color="purple.400" boxSize={3} />
                  </Circle>
                  <Text marginBottom={0} {...textXSBoldStyle}>
                    <strong>9 von 10 Eigentümern</strong> werden erreicht – dank
                    optimalem Anrufzeitpunkt
                  </Text>
                </Flex>
                <Flex>
                  <Circle
                    marginTop={1}
                    marginRight={2}
                    backgroundColor="purple.25"
                    boxSize={4}
                  >
                    <Check color="purple.400" boxSize={3} />
                  </Circle>
                  <Text marginBottom={0} {...textXSBoldStyle}>
                    <strong>Bis zu 26% mehr Bewertungstermine</strong> durch
                    analysierte Gespräche
                  </Text>
                </Flex>
                <Flex>
                  <Circle
                    marginTop={1}
                    marginRight={2}
                    backgroundColor="purple.25"
                    boxSize={4}
                  >
                    <Check color="purple.400" boxSize={3} />
                  </Circle>
                  <Text marginBottom={0} {...textXSBoldStyle}>
                    <strong>Termine direkt im Kalender –</strong> keine
                    Nacharbeit, keine Abstimmungsschleifen
                  </Text>
                </Flex>
              </VStack>
              <Button
                as={GatsbyLink}
                to="/koop-anfrage/"
                onClick={handleClick}
                marginTop={8}
                {...buttonStyle}
              >
                Kostenlosen Zugang sichern
              </Button>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* Section 3 - How to */}
      <Box
        textColor="white"
        backgroundRepeat="no-repeat"
        backgroundBlendMode="soft-light"
        backgroundPosition="top"
        backgroundSize="cover"
        /*
         * The used chakra-ui version has a bug which would transform "url" to "url-gradiant" which is invalid.
         * So, instead of using "backgroundImage" directly we use "style" instead.
         * See https://github.com/chakra-ui/chakra-ui/issues/7548
         */
        style={{
          backgroundImage: `url(/heyemi/bg-section3.png),${purpleGradient}`,
        }}
        {...sectionStyle}
      >
        <Container marginBottom={6}>
          <Box>
            <HeadingDisplay
              as="h2"
              textColor="white"
              justifySelf="center"
              {...headlineStyle}
            >
              Und so einfach funktioniert&apos;s:
            </HeadingDisplay>
            <Grid
              templateColumns={{ base: '1fr', xl: 'repeat(20, 1fr)' }}
              templateAreas={{
                base: `
            'iconSpeechTelephone'
            'textSpeechTelephone'
            'arrow1'
            'iconCalendarUser'
            'textCalendarUser'
            'arrow2'
            'iconHandshakeTarget'
            'textHandshakeTarget'
            `,
                xl: `
            '. iconSpeechTelephone iconSpeechTelephone iconSpeechTelephone iconSpeechTelephone arrow1 arrow1 arrow1 iconCalendarUser iconCalendarUser iconCalendarUser iconCalendarUser arrow2 arrow2 arrow2 iconHandshakeTarget iconHandshakeTarget iconHandshakeTarget iconHandshakeTarget .'
            'textSpeechTelephone textSpeechTelephone textSpeechTelephone textSpeechTelephone textSpeechTelephone textSpeechTelephone . textCalendarUser textCalendarUser textCalendarUser textCalendarUser textCalendarUser textCalendarUser . textHandshakeTarget textHandshakeTarget textHandshakeTarget textHandshakeTarget textHandshakeTarget textHandshakeTarget'
          `,
              }}
              paddingX={10}
              paddingTop={{ base: 4, xl: 0 }}
              rowGap={4}
              justifyItems="center"
            >
              <Relative gridArea="iconSpeechTelephone">
                <SpeechTelephone />
                <Absolute
                  top={-3}
                  right={-3}
                  boxSize={9}
                  style={{
                    transform: 'translateZ(0)', // Safari rendering fix
                  }}
                >
                  <Circle {...iconCircleStyle} textColor="black">
                    01
                  </Circle>
                </Absolute>
              </Relative>
              <Box
                gridArea="arrow1"
                alignContent="center"
                display="flex"
                justifyContent="center"
                alignItems="center"
                transform={{ base: 'rotate(90deg)', xl: 'none' }}
                minHeight="100px"
                style={{
                  transformOrigin: 'center center',
                  WebkitTransformOrigin: 'center center',
                }}
              >
                <Arrow width={isDesktopView ? 'auto' : '100px'} color="white" />
              </Box>
              <Relative gridArea="iconCalendarUser">
                <CalendarUser />
                <Absolute
                  top={-3}
                  right={-3}
                  boxSize={9}
                  style={{
                    transform: 'translateZ(0)', // Safari rendering fix
                  }}
                >
                  <Circle {...iconCircleStyle} textColor="black">
                    02
                  </Circle>
                </Absolute>
              </Relative>
              <Box
                gridArea="arrow2"
                alignContent="center"
                display="flex"
                justifyContent="center"
                alignItems="center"
                transform={{ base: 'rotate(90deg)', xl: 'none' }}
                minHeight="100px"
                style={{
                  transformOrigin: 'center center',
                  WebkitTransformOrigin: 'center center',
                }}
              >
                <Arrow width={isDesktopView ? 'auto' : '100px'} color="white" />
              </Box>
              <Relative gridArea="iconHandshakeTarget">
                <HandshakeTarget />
                <Absolute
                  top={-3}
                  right={-3}
                  boxSize={9}
                  style={{
                    transform: 'translateZ(0)', // Safari rendering fix
                  }}
                >
                  <Circle {...iconCircleStyle} textColor="black">
                    03
                  </Circle>
                </Absolute>
              </Relative>
              <Box gridArea="textSpeechTelephone" textAlign="center">
                <Box {...textLGBoldStyle} textColor="white" paddingBottom={2}>
                  emi ruft Ihren Interessenten an
                </Box>
                <Box {...textXSBoldStyle} textColor="white">
                  Zum perfekten Zeitpunkt persönlich & informiert
                </Box>
              </Box>
              <Box gridArea="textCalendarUser" textAlign="center">
                <Box {...textLGBoldStyle} textColor="white" paddingBottom={2}>
                  emi trägt den Termin in Ihren Kalender ein
                </Box>
                <Box {...textXSBoldStyle} textColor="white">
                  Direkte Bestätigung für Sie und Ihren Kunden
                </Box>
              </Box>
              <Box gridArea="textHandshakeTarget" textAlign="center">
                <Box {...textLGBoldStyle} textColor="white" paddingBottom={2}>
                  Sie bewerten und schließen den Vertrag
                </Box>
                <Box {...textXSBoldStyle} textColor="white">
                  Weniger Verwaltung, mehr Kundenfokus, mehr Abschlüsse
                </Box>
              </Box>
            </Grid>
          </Box>
          <Box textAlign="center" marginTop={{ base: 8, [BP_DESKTOP]: 12 }}>
            <Button
              as={GatsbyLink}
              to="/koop-anfrage/"
              onClick={handleClick}
              {...buttonStyle}
            >
              Kostenlosen Zugang sichern
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Section 4 - How to */}
      <Box {...sectionStyle}>
        <Container marginBottom={6}>
          <HeadingDisplay
            as="h2"
            justifySelf="center"
            {...headlineStyle}
            textColor="purple.400"
            textAlign="center"
          >
            So wird heyemi zur Nr. 1 für die Objektaquise
          </HeadingDisplay>
          <Grid
            gridTemplateAreas={{
              base: `
            'numberExperience'
            'headlineExperience'
            'descriptionExperience'
            'numberActiveBroker'
            'headlineActiveBroker'
            'descriptionActiveBroker'
            'numberExclusiveData'
            'headlineExclusiveData'
            'descriptionExclusiveData'
            `,
              xl: `
            'numberExperience numberActiveBroker numberExclusiveData'
            'headlineExperience headlineActiveBroker headlineExclusiveData'
            'descriptionExperience descriptionActiveBroker descriptionExclusiveData'
          `,
            }}
            paddingX={10}
            paddingTop={{ base: 4, xl: 0 }}
            rowGap={0}
            columnGap={4}
            textAlign="center"
          >
            <Box
              gridArea="numberExperience"
              {...textXLBoldStyle}
              textColor="purple.400"
              paddingTop={{ base: 6, xl: 0 }}
            >
              10+
            </Box>
            <Box
              gridArea="headlineExperience"
              {...textLGBoldStyle}
              textColor="purple.400"
              paddingBottom={2}
            >
              Jahre Erfahrung
            </Box>
            <Box gridArea="descriptionExperience" {...textXSBoldStyle}>
              Ein Jahrzehnt Expertise in erfolgreicher Objektakquise für Makler.
            </Box>

            <Box
              gridArea="numberActiveBroker"
              {...textXLBoldStyle}
              textColor="purple.400"
              paddingTop={{ base: 6, xl: 0 }}
            >
              3.000+
            </Box>
            <Box
              gridArea="headlineActiveBroker"
              {...textLGBoldStyle}
              textColor="purple.400"
              paddingBottom={2}
            >
              aktive Makler
            </Box>
            <Box gridArea="descriptionActiveBroker" {...textXSBoldStyle}>
              Tausende Makler arbeiten bereits erfolgreich mit HAUSGOLD
              zusammen.
            </Box>

            <Box
              gridArea="numberExclusiveData"
              {...textXLBoldStyle}
              textColor="purple.400"
              paddingTop={{ base: 6, xl: 0 }}
            >
              1 Mio.+
            </Box>
            <Box
              gridArea="headlineExclusiveData"
              {...textLGBoldStyle}
              textColor="purple.400"
              paddingBottom={2}
            >
              exklusive Trainingsdaten
            </Box>
            <Box gridArea="descriptionExclusiveData" {...textXSBoldStyle}>
              heyemi ist mit über einer Million Akquise-Daten und weiteren
              Marktdaten trainiert.
            </Box>
          </Grid>
        </Container>
      </Box>

      {/* Section 5 - Who are you */}
      <Box {...sectionStyle} backgroundColor="#0A1F44">
        <Container>
          <Flex
            textColor="white"
            flexDirection={{ base: 'column', xl: 'row' }}
            alignItems="center"
          >
            <Box
              width={{ base: '100%', xl: '40%' }}
              marginRight={{ base: 0, xl: 10 }}
            >
              <Flex width="190px" alignItems="end">
                <EmiLogo height="60" />
                <Box
                  as="span"
                  {...headlineStyle}
                  fontSize="3rem"
                  marginBottom={0}
                >
                  ,
                </Box>
              </Flex>
              <HeadingDisplay as="h2" {...headlineStyle} fontSize="3rem">
                wer bist du?
              </HeadingDisplay>
            </Box>
            <Box fontSize="xl" width={{ base: '100%', xl: '60%' }}>
              <Box {...textLGBoldStyle} marginBottom={8}>
                “Ich bin Ihre <u>e</u>ffiziente, <u>m</u>odulare <u>I</u>
                ntelligenz.
              </Box>
              <Box {...textSMStyle} marginBottom={8}>
                Mein gesamtes Wissen setze ich ein, um Sie bei der Akquise zu
                unterstützen. Ich übernehme die Lead-Bearbeitung, liefere
                datengestützte Empfehlungen und wachse mit Ihren Anforderungen.
              </Box>
              <Box {...textSMStyle}>
                Jetzt starte ich mit der automatisierten Telefonie – weitere
                smarte Funktionen folgen.”
              </Box>
            </Box>
          </Flex>

          <Box
            textColor="white"
            textAlign="center"
            marginTop={{ base: 12, [BP_DESKTOP]: 16 }}
          >
            <Box {...textLGBoldStyle} marginBottom={0}>
              Sprechen Sie einfach direkt mit mir:
            </Box>
            <Link
              textColor="orange.400"
              _hover={{ textColor: 'orange.300' }}
              href="tel:04074303045"
              fontSize={{ base: '2rem', [BP_DESKTOP]: '3rem' }}
              fontWeight="bold"
            >
              040 74303045
            </Link>
            <Box {...textSMStyle}>
              (Rufen Sie emi kostenlos 24/7 an und stellen Sie Ihre Fragen)
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Section 6 - Pricing */}
      <Box
        {...sectionStyle}
        backgroundImage="/heyemi/bg-construction.png"
        backgroundRepeat="no-repeat"
        backgroundPosition="bottom"
        backgroundSize="cover"
      >
        <Container>
          <Flex
            flexDirection={{ base: 'column', xl: 'row' }}
            justifyContent="space-between"
          >
            <Box
              textColor="white"
              maxWidth={{ base: '100%', xl: '30%' }}
              flexGrow={0}
            >
              <HeadingDisplay
                as="h2"
                {...headlineStyle}
                marginBottom={{ base: 4, xl: 6 }}
              >
                Kostenlos starten
              </HeadingDisplay>
              <Text {...textLGStyle}>Wir optimieren gemeinsam.</Text>
            </Box>
            <Box
              backgroundColor={purpleColor}
              padding={8}
              borderRadius="2xl"
              textColor="white"
              maxWidth={{ base: '100%', xl: '50%', '2xl': '55%' }}
            >
              <Flex
                flexDirection={{ base: 'column', xl: 'row' }}
                marginBottom={8}
              >
                <Box
                  textAlign={{ base: 'center', xl: 'left' }}
                  alignContent="center"
                >
                  <Box {...textSMStyle} marginBottom={0}>
                    Testphase
                  </Box>
                  <Box fontWeight="bold">
                    <Text as="span" {...priceStyle}>
                      0€
                    </Text>
                    <Box as="span" {...textSMAlwaysMDBolder}>
                      &nbsp;/&nbsp;Monat
                    </Box>
                  </Box>
                </Box>
                <Box justifyItems="center" alignContent="center">
                  <Divider
                    orientation="vertical"
                    display={{ base: 'none', xl: 'block' }}
                    height="80%"
                    marginX={4}
                  />
                  <Divider
                    orientation="horizontal"
                    display={{ base: 'block', xl: 'none' }}
                    width="20%"
                    marginY={4}
                  />
                </Box>
                <Box>
                  <List spacing={4}>
                    <ListItem>
                      <Flex alignItems="center">
                        <ListIcon as={CheckCircled} {...listIconStyle} />
                        <Box {...textSMAlwaysBolder}>
                          Testphase: Aktuell noch kostenlos
                        </Box>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex alignItems="center">
                        <ListIcon as={CheckCircled} {...listIconStyle} />
                        <Box {...textSMAlwaysBolder}>
                          Erst Mehrwert schaffen, Preismodell folgt später
                        </Box>
                      </Flex>
                    </ListItem>
                    <ListItem>
                      <Flex alignItems="center">
                        <ListIcon as={CheckCircled} {...listIconStyle} />
                        <Box {...textSMAlwaysBolder}>
                          Flexibel bleiben, keine Mindestlaufzeit
                        </Box>
                      </Flex>
                    </ListItem>
                  </List>
                </Box>
              </Flex>
              <Button
                as={GatsbyLink}
                to="/koop-anfrage/"
                onClick={handleClick}
                {...buttonStyle}
              >
                Kostenlosen Zugang sichern
              </Button>
            </Box>
          </Flex>
        </Container>
      </Box>

      {exitIntent && (
        <ExitIntentModal
          show={showPopup}
          image={exitIntent.image}
          text={exitIntent.text.childMarkdownRemark.html}
          button={exitIntent.button}
          setShowPopup={setShowPopup}
        />
      )}
    </Layout>
  );
};

export default Heyemi;

export const pageQuery = graphql`
  query HeyEmi {
    site {
      siteMetadata {
        title
        siteUrl
      }
    }
    contentfulPage(slug: { eq: "heyemi" }) {
      useArticleSchema
      contentful_id
      id
      title
      headline
      headlineColor
      subTitle
      indexable
      metaTitle
      metaDescription
      slug
      updatedAt
      createdAt
      childPages {
        id
        slug
        title
      }
      page {
        id
        slug
        title
        childPages {
          id
          slug
          title
        }
      }
      exitIntent {
        image {
          gatsbyImageData(layout: FIXED, width: 170)
          title
        }
        text {
          childMarkdownRemark {
            html
          }
        }
        button {
          title
          url
          color
          size
          shadow
        }
      }
    }
  }
`;
